// Patient milestone badges: the approved wording, the badge catalogue, and the rules every reader uses.
// ONE source, spliced into every copy by scripts/sync-milestone-kit.mjs (S-175 compares them).
// Spec: docs/specs/2026-10-09-patient-milestone-badges.md. ASCII only: the regions are spliced into
// workflow JSON files that store non-ASCII as \u escapes.

// MILESTONE-COPY-START
// The approved wording (spec D10). APPEND-ONLY per badge: a change is a new version, and old versions stay
// so old rows keep matching. ", {first_name}" disappears with no usable name; "{devices}" takes one
// DEVICE_PHRASES value. Every version is: title, lead, "Header:" + bullets, optional footer.
const MILESTONE_KIT = 'access-badges/v1';
const MILESTONE_BADGES = Object.freeze({
  milestone_devices_shipped: Object.freeze({ key: 'devices_shipped', name: 'Devices shipped', art: 'devices_shipped.svg', accent: '#3E9BB5' }),
  milestone_first_reading: Object.freeze({ key: 'first_reading', name: 'First reading received', art: 'first_reading.svg', accent: '#5168CC' }),
});
const MILESTONE_COPY = {
  milestone_devices_shipped: [
    {
      version: 1,
      text: 'Your devices are on the way!\n\n'
        + 'Good news, {first_name}. Your care team has shipped your {devices}.\n\n'
        + 'What happens next:\n'
        + '\u2022 Allow a few days; a tracking email follows.\n'
        + '\u2022 There is nothing to install: just turn the device on.\n'
        + '\u2022 Take one test reading to earn your next badge.',
    },
  ],
  milestone_first_reading: [
    {
      version: 1,
      text: 'You\u2019re connected!\n\n'
        + 'Congratulations, {first_name}. Your readings now reach your care team.\n\n'
        + 'Why it matters:\n'
        + '\u2022 Your care team uses your readings to catch problems early, coordinate your care, and keep you on track with your health goals.\n'
        + '\u2022 Between visits, our monitoring system is an extra set of eyes: it flags readings that may need a closer look so your care team can follow up. It does not watch around the clock.\n'
        + '\u2022 The more regularly you measure, the clearer the picture for your care team.\n\n'
        + 'Electronic messages are not a substitute for professional medical care. For a medical emergency, call 911.',
    },
  ],
};
for (const k of Object.keys(MILESTONE_COPY)) { MILESTONE_COPY[k].forEach(Object.freeze); Object.freeze(MILESTONE_COPY[k]); }
Object.freeze(MILESTONE_COPY);
// A closed list: a care-team free-text override on care_plans.devices is never echoed to a patient.
const DEVICE_PHRASES = Object.freeze({
  both: 'BPM Pro 2 blood pressure monitor and Body Pro 2 scale',
  bpm: 'BPM Pro 2 blood pressure monitor',
  scale: 'Body Pro 2 scale',
  generic: 'Withings devices',
});
function milestoneOwn(obj, key) {
  return typeof key === 'string' && Object.prototype.hasOwnProperty.call(obj, key);
}
function devicesPhrase(list) {
  if (!Array.isArray(list) || !list.length) return DEVICE_PHRASES.generic;
  let bpm = false;
  let scale = false;
  for (const d of list) {
    const n = String(d == null ? '' : d).trim().toLowerCase().replace(/\s+/g, ' ');
    if (n === 'bpm pro 2') bpm = true;
    else if (n === 'body pro 2') scale = true;
    else return DEVICE_PHRASES.generic;
  }
  if (bpm && scale) return DEVICE_PHRASES.both;
  return bpm ? DEVICE_PHRASES.bpm : DEVICE_PHRASES.scale;
}
// Latin letters only (no look-alike punctuation from other scripts, no combining marks), single
// apostrophes, hyphens or spaces between letters, two spaces and 30 characters at most, equal to its
// NFKC form.
// Anything else -> no name ("Good news." / "Congratulations."), never a guess.
const FIRST_NAME_RE = /^(?=\p{Script=Latin})\p{L}(?:['\- ]?(?=\p{Script=Latin})\p{L})*$/u;
function cleanFirstName(raw) {
  if (typeof raw !== 'string') return null;
  const t = raw.normalize('NFKC').replace(/\u2019/g, "'").replace(/\s+/g, ' ').trim();
  return t.length <= 30 && (t.match(/ /g) || []).length <= 2 && FIRST_NAME_RE.test(t) ? t : null;
}
function fillMilestoneCopy(text, firstName, devices) {
  const name = cleanFirstName(firstName);
  return text.split(', {first_name}').join(name ? ', ' + name : '').split('{devices}').join(devicesPhrase(devices));
}
// msgType + { firstName, devices } -> { text, version } with the latest version, or null.
function buildMilestoneBody(msgType, opts) {
  const versions = milestoneOwn(MILESTONE_COPY, msgType) ? MILESTONE_COPY[msgType] : null;
  if (!versions || !versions.length) return null;
  const latest = versions[versions.length - 1];
  const o = opts || {};
  return { text: fillMilestoneCopy(latest.text, o.firstName, o.devices), version: latest.version };
}
// MILESTONE-COPY-END

// MILESTONE-MATCH-START
// A row is drawn as a badge only when its body equals a known version of ITS OWN badge's wording, with
// valid slot values (spec 7). Anything else -> null, and the caller shows the plain text. The model's
// words are rebuilt from the template, never echoed from the body.
const MILESTONE_ESC_RE = /[\\^$.*+?()[\]{}|/]/g;
const milestoneEsc = (s) => s.replace(MILESTONE_ESC_RE, '\\$&');
const MILESTONE_NAME_SLOT = "(?:, (?<first_name>\\p{L}[\\p{L}\\p{M}' -]{0,29}))?";
const MILESTONE_DEVICE_SLOT = '(?<devices>' + [DEVICE_PHRASES.both, DEVICE_PHRASES.bpm, DEVICE_PHRASES.scale, DEVICE_PHRASES.generic].map(milestoneEsc).join('|') + ')';
function milestonePattern(text) {
  const parts = text.split(', {first_name}').map((seg) => seg.split('{devices}').map(milestoneEsc).join(MILESTONE_DEVICE_SLOT));
  return new RegExp('^' + parts.join(MILESTONE_NAME_SLOT) + '$', 'u');
}
function isMilestoneRow(row) {
  const r = row || {};
  return r.direction === 'outbound' && r.source === 'system' && milestoneOwn(MILESTONE_BADGES, r.msg_type);
}
function matchMilestone(row, opts) {
  try {
    if (!isMilestoneRow(row) || typeof row.body !== 'string' || row.body.length > 2000) return null;
    const versions = MILESTONE_COPY[row.msg_type];
    for (let i = versions.length - 1; i >= 0; i -= 1) {
      const m = milestonePattern(versions[i].text).exec(row.body);
      if (!m) continue;
      const g = m.groups || {};
      const firstName = g.first_name != null ? g.first_name : null;
      if (firstName != null && cleanFirstName(firstName) !== firstName) return null;
      // A client that knows the patient's first name draws only a row carrying that name (or none).
      if (opts && Object.prototype.hasOwnProperty.call(opts, 'expectedFirstName') && firstName != null
        && firstName.toLowerCase() !== String(cleanFirstName(opts.expectedFirstName) || '').toLowerCase()) return null;
      const text = versions[i].text.split(', {first_name}').join(firstName ? ', ' + firstName : '').split('{devices}').join(g.devices || '');
      if (text !== row.body) return null;
      const blocks = text.split('\n\n');
      const list = blocks[2].split('\n');
      const badge = MILESTONE_BADGES[row.msg_type];
      return Object.freeze({
        msgType: row.msg_type,
        key: badge.key,
        name: badge.name,
        art: badge.art,
        accent: badge.accent,
        version: versions[i].version,
        firstName,
        devices: g.devices != null ? g.devices : null,
        title: blocks[0],
        lead: blocks[1],
        header: list[0].replace(/:$/, ''),
        items: Object.freeze(list.slice(1).map((l) => l.replace(/^\u2022 /, ''))),
        footer: blocks.length > 3 ? blocks[3] : null,
      });
    }
    return null;
  } catch (e) {
    return null;
  }
}
// MILESTONE-MATCH-END

// MILESTONE-LABEL-START
// Every reader that feeds a model sees a milestone row as a label, never its body (class #29, spec 11).
// Keyed on msg_type: hiding our own body from a model is always safe. "Answers nothing" (WF2 lane 4)
// additionally needs source 'system', so a coordinator's row with that msg_type still answers.
const MILESTONE_LABELS = Object.freeze({
  milestone_devices_shipped: '[milestone sent: devices shipped]',
  milestone_first_reading: "[milestone sent: you're connected]",
});
// Never on a patient's own row: a forged msg_type there must not hide their words from a check.
function milestoneLabelOf(row) {
  const mt = row && row.msg_type;
  if (!row || row.direction === 'inbound' || row.source === 'patient' || row.sender === 'patient') return null;
  return (typeof mt === 'string' && Object.prototype.hasOwnProperty.call(MILESTONE_LABELS, mt)) ? MILESTONE_LABELS[mt] : null;
}
function isMilestoneSystemRow(row) {
  return milestoneLabelOf(row) !== null && row.source === 'system';
}
// WF80 tags each badge's chat row and its "new message" ping with this prefix + the badge key, so the
// back office can pair them and WF26 can leave the ping out of its own 3 h count.
const MILESTONE_TRIGGER_PREFIX = 'wf80_milestone';
function isMilestonePing(row) {
  return !!row && row.msg_type === 'chat_notification' && String(row.trigger_source || '').startsWith(MILESTONE_TRIGGER_PREFIX);
}
// The row as a model may see it: a milestone row's body replaced by its label, any other row untouched.
function labelMilestoneRow(row) {
  const label = milestoneLabelOf(row);
  return label ? Object.assign({}, row, { body: label }) : row;
}
// MILESTONE-LABEL-END

// MILESTONE-SWITCH-START
// $vars.PATIENT_MILESTONES (spec 6.1): one comma-separated list of words for every milestone. A badge key
// turns that badge on, `sms` adds WF26's standard ping, `only:<uid>` (repeatable) limits everything to those
// patients. Fail-closed: absent, empty, `off` or 'true' turns nothing on, and ONE unknown word turns EVERYTHING
// off (a mistyped `only:` must never widen a pilot to every patient). A new badge adds a word here (S-175
// compares this list with the catalogue), never a variable.
const MILESTONE_SWITCH_BADGES = Object.freeze(['devices_shipped', 'first_reading']);
function parseMilestoneSwitch(raw) {
  const cfg = { badges: [], sms: false, only: null, unknown: [] };
  if (typeof raw !== 'string') return cfg;
  for (const part of raw.split(',')) {
    const w = part.trim();
    // `off` is how the variable is created (n8n may refuse an empty value): a known word that turns nothing on.
    if (!w || w === 'off') continue;
    if (w === 'sms') cfg.sms = true;
    else if (MILESTONE_SWITCH_BADGES.indexOf(w) >= 0) { if (cfg.badges.indexOf(w) < 0) cfg.badges.push(w); }
    else if (/^only:[1-9]\d{0,18}$/.test(w)) { cfg.only = cfg.only || []; cfg.only.push(w.slice(5)); }
    // Logged with its digits masked (a mistyped only: carries a patient id), short.
    else cfg.unknown.push(w.replace(/[^A-Za-z0-9_:-]/g, '').replace(/[0-9]/g, '#').slice(0, 40));
  }
  if (cfg.unknown.length) return { badges: [], sms: false, only: null, unknown: cfg.unknown };
  return cfg;
}
function milestoneSwitchAllows(cfg, key, uid) {
  return !!cfg && Array.isArray(cfg.badges) && cfg.badges.indexOf(key) >= 0
    && (cfg.only == null || cfg.only.indexOf(String(uid)) >= 0);
}
// MILESTONE-SWITCH-END

// MILESTONE-HINTS-START
// WF5's hints for the connected badge (spec 5.1), built from WF3's node output ONLY: WF5's door is
// unauthenticated, so nothing the caller sent may reach WF80. Scalars only. Only the reading WF3 actually
// classified counts: its BP chain runs on appli 4 only, and the group must be the one it classified
// (bp_classified + bp_grpid). Needs region S.
function milestoneHintsFromWf3(wf3, eventUid) {
  const out = { ok: false };
  const ctx = wf3 && typeof wf3 === 'object' ? wf3.context : null;
  if (!ctx || typeof ctx !== 'object') return out;
  const uid = String(ctx.withings_user_id == null ? '' : ctx.withings_user_id);
  if (!/^[1-9]\d{0,18}$/.test(uid) || uid !== String(eventUid == null ? '' : eventUid)) return out;
  const exc = Array.isArray(wf3.exceptions) && wf3.exceptions.length ? wf3.exceptions[0] : null;
  // The BP reading is the group (grpid) holding both systolic (10) and diastolic (9): WF4's newest second
  // can also hold a scale's groups.
  const groups = Object.create(null);
  for (const m of (Array.isArray(ctx.newMeasures) ? ctx.newMeasures : [])) {
    if (!m || m.grpid == null) continue;
    const k = String(m.grpid);
    const g = groups[k] || (groups[k] = { types: Object.create(null), date: m.date, attrib: m.attrib });
    g.types[String(m.type)] = true;
  }
  const classified = wf3.bp_classified === true && wf3.bp_grpid != null;
  const g0 = classified ? groups[String(wf3.bp_grpid)] : null;
  const bp = g0 && g0.types['9'] && g0.types['10'] ? g0 : null;
  const at = bp ? Date.parse(String(bp.date || '')) : null;
  const attrib = bp && bp.attrib != null && bp.attrib !== '' ? Number(bp.attrib) : null;
  return {
    ok: true,
    fetch_ok: ctx.fetch_ok === true,
    bp_classified: !!bp,
    uid,
    exc_type: exc && exc.type != null ? String(exc.type) : null,
    exc_band: exc && exc.band != null ? String(exc.band) : null,
    bp_group_at: at != null && Number.isFinite(at) ? at : null,
    bp_group_attrib: Number.isInteger(attrib) ? attrib : null,
    care_period_id: ctx.carePeriodId != null ? String(ctx.carePeriodId) : null,
    care_period_status: ctx.carePeriodStatus != null ? String(ctx.carePeriodStatus) : null,
  };
}
// WF5 calls WF80 only for these: a weight reading, a closed or pending period, or the switch off spawns nothing.
function milestoneReadingCandidate(h, cfg) {
  return !!h && h.ok === true && h.fetch_ok === true && h.bp_group_at != null && h.care_period_status === 'active'
    && milestoneSwitchAllows(cfg, 'first_reading', h.uid);
}
// MILESTONE-HINTS-END

// MILESTONE-GATE-START
// WF80's door (spec 6.1): the trigger's inputs -> { ok: true, item } or { ok: false, reason }. Never throws:
// WF5's door is unauthenticated, so a throw here would flood WF14. Needs region S.
const MILESTONE_ID_RE = /^[1-9]\d{0,18}$/;
// D11 (clinical sign-off pending): no exception, or uncontrolled only. Any other type blocks, known or not.
const MILESTONE_READING_ALLOW = Object.freeze([null, 'bp_uncontrolled']);
// D8: typed into the app, never measured by a device (the fleet rule of WF37 and WF53).
const MILESTONE_MANUAL_ATTRIBS = Object.freeze([2, 4]);
// Except on BETA (SAT, 2026-10-09: typed-in readings make testing possible without a paired cuff). An
// allowlist of the BETA program, so PROD (6720) can never count one, whatever its variables say.
const MILESTONE_MANUAL_OK_PROGRAMS = Object.freeze(['6719']);
function milestoneGate(raw, cfg, programId) {
  try {
    const r = raw && typeof raw === 'object' ? raw : {};
    const key = String(r.milestone == null ? '' : r.milestone);
    if (MILESTONE_SWITCH_BADGES.indexOf(key) < 0) return { ok: false, reason: 'unknown_milestone' };
    const uid = String(r.withings_user_id == null ? '' : r.withings_user_id).trim();
    if (!MILESTONE_ID_RE.test(uid)) return { ok: false, reason: 'bad_uid' };
    if (!milestoneSwitchAllows(cfg, key, uid)) return { ok: false, reason: 'switched_off' };
    const item = { milestone: key, msg_type: 'milestone_' + key, withings_user_id: uid, sms: !!(cfg && cfg.sms) };
    if (key === 'devices_shipped') {
      const cp = String(r.care_period_id == null ? '' : r.care_period_id).trim();
      const task = String(r.source_task_id == null ? '' : r.source_task_id).trim();
      if (!MILESTONE_ID_RE.test(cp) || !MILESTONE_ID_RE.test(task)) return { ok: false, reason: 'bad_ids' };
      item.care_period_id = cp;
      item.source_task_id = task;
      return { ok: true, item };
    }
    let h = null;
    try { h = typeof r.hints === 'string' ? JSON.parse(r.hints) : null; } catch (e) { h = null; }
    if (!h || typeof h !== 'object' || Array.isArray(h)) return { ok: false, reason: 'no_hints' };
    if (h.ok !== true || h.fetch_ok !== true) return { ok: false, reason: 'hints_not_ok' };
    if (h.bp_classified !== true) return { ok: false, reason: 'reading_not_classified' };
    if (String(h.uid) !== uid) return { ok: false, reason: 'hints_uid_mismatch' };
    if (typeof h.bp_group_at !== 'number' || !Number.isFinite(h.bp_group_at)) return { ok: false, reason: 'no_bp_group' };
    const manualOk = MILESTONE_MANUAL_OK_PROGRAMS.indexOf(String(programId == null ? '' : programId)) >= 0;
    if (!Number.isInteger(h.bp_group_attrib) || (!manualOk && MILESTONE_MANUAL_ATTRIBS.indexOf(h.bp_group_attrib) >= 0)) return { ok: false, reason: 'not_device_measured' };
    const exc = h.exc_type == null ? null : String(h.exc_type);
    if (MILESTONE_READING_ALLOW.indexOf(exc) < 0) return { ok: false, reason: 'reading_not_allowed' };
    const cp = String(h.care_period_id == null ? '' : h.care_period_id);
    if (!MILESTONE_ID_RE.test(cp)) return { ok: false, reason: 'no_period' };
    if (h.care_period_status !== 'active') return { ok: false, reason: 'period_not_active' };
    item.care_period_id = cp;
    item.bp_group_at = h.bp_group_at;
    return { ok: true, item };
  } catch (e) {
    return { ok: false, reason: 'gate_error' };
  }
}
// MILESTONE-GATE-END

// MILESTONE-DECIDE-START
// WF80's two lanes, pure (spec 4, 5.2, 6.4, 6.5). Every doubt holds: a missed badge costs nothing, a wrong one
// lands next to an alert. Needs region C.
const MILESTONE_RULES = Object.freeze({
  shipWindowDays: 21, lookbackHours: 24, lockTemplate: 'sys_patient_milestone', triggerPrefix: 'wf80_milestone',
});
const MILESTONE_OPEN_TASK = Object.freeze(['pending', 'scheduled', 'in_progress', 'overdue']);
// The page sizes WF80's reads ask for (S-175 compares them with the nodes): a full page may be truncated, so it holds.
const MILESTONE_LIMITS = Object.freeze({ periods: 50, plans: 50, profiles: 2, urgent: 50, recent: 200, ship: 20 });
// A ship task stamped a few minutes ahead of this clock (a caller-supplied completed_at) still counts.
const MILESTONE_CLOCK_SKEW_MS = 5 * 60000;
const milestoneOwner = (r) => String(r.withings_user_id != null ? r.withings_user_id : r.id_dev_user);
const milestonePeriodRef = (r) => String(r.id_care_period != null ? r.id_care_period : (r.care_period_id != null ? r.care_period_id : ''));
// The patient's own rows of a list read, or null when the read cannot be trusted: degraded, truncated (a count
// above the rows, or a full page), or carrying another patient's row (the filter was not applied, class #20).
function milestoneRows(env, uid, limit) {
  if (!env || env.status !== 'success') return null;
  const data = Array.isArray(env.data) ? env.data : (env.data && typeof env.data === 'object' ? [env.data] : null);
  if (!data) return null;
  const count = Number(env.meta && env.meta.count);
  if (Number.isFinite(count) && count > data.length) return null;
  if (limit != null && data.length >= limit) return null;
  if (data.some((r) => !r || milestoneOwner(r) !== String(uid))) return null;
  return data;
}
function milestoneEligiblePeriod(env, uid, cp, programId) {
  const rows = milestoneRows(env, uid, MILESTONE_LIMITS.periods);
  if (!rows) return { ok: false, reason: 'period_read_degraded' };
  // Only Care_Period rows carry care_period_id (class #15); the filter proves nothing about ownership (#20).
  const p = rows.find((r) => String(r.care_period_id != null ? r.care_period_id : r.id_care_period) === String(cp));
  if (!p) return { ok: false, reason: 'period_not_found' };
  if (!programId || String(p.programid) !== String(programId)) return { ok: false, reason: 'foreign_program' };
  if (String(p.status || '') !== 'active') return { ok: false, reason: 'period_not_active' };
  if (!p.consent_obtained_at) return { ok: false, reason: 'no_consent' };
  return { ok: true, consent_obtained_at: String(p.consent_obtained_at) };
}
// The completed ship task on this period, inside the D6 window. A cancelled task never qualifies (D5).
function milestoneShipTask(rows, uid, cp, nowMs, taskId) {
  const mine = rows.filter((t) => String(t.template_code || '') === 'onb_device_ship' && milestoneOwner(t) === String(uid)
    && milestonePeriodRef(t) === String(cp) && (taskId == null || String(t.task_id != null ? t.task_id : t.id_task_instance) === String(taskId)));
  if (!mine.length) return { ok: false, reason: 'ship_task_not_found' };
  const completed = mine.filter((t) => String(t.status || '') === 'completed');
  if (!completed.length) return { ok: false, reason: 'ship_not_completed' };
  // A patient session can complete its own ship task while WF17 does not bind ownership (spec 13, R1).
  const done = completed.filter((t) => String(t.completion_source || '') !== 'patient');
  if (!done.length) return { ok: false, reason: 'ship_completed_by_patient' };
  let best = null;
  for (const t of done) {
    const at = Date.parse(String(t.completed_at || ''));
    if (Number.isFinite(at) && (!best || at > best.at)) best = { at, task_id: String(t.task_id != null ? t.task_id : t.id_task_instance) };
  }
  if (!best) return { ok: false, reason: 'ship_undated' };
  if (best.at > nowMs + MILESTONE_CLOCK_SKEW_MS || nowMs - best.at > MILESTONE_RULES.shipWindowDays * 86400000) return { ok: false, reason: 'ship_outside_window' };
  return { ok: true, completed_at: best.at, task_id: best.task_id };
}
// What we ship, from the latest care plan, through the closed list in region C. Unreadable -> the generic
// phrase: a care-team free-text override is never echoed.
function milestoneDevicesOf(env, uid) {
  const rows = milestoneRows(env, uid, MILESTONE_LIMITS.plans);
  if (!rows || !rows.length) return null;
  // The current plan when one is marked, never a draft or another period's plan.
  const current = rows.filter((r) => r.is_current === true || r.is_current === 't' || r.is_current === 1);
  let best = null;
  for (const r of (current.length ? current : rows)) {
    const v = Number(r.version);
    if (!best || (Number.isFinite(v) && v > Number(best.version))) best = r;
  }
  let d = best && best.devices;
  if (typeof d === 'string') {
    const s = d.trim();
    try { d = s.charAt(0) === '[' ? JSON.parse(s) : s.replace(/^\{|\}$/g, '').split(',').map((x) => x.trim().replace(/^"|"$/g, '')).filter(Boolean); } catch (e) { d = null; }
  }
  return Array.isArray(d) ? d : null;
}
function milestoneProfileOf(env, uid) {
  const rows = milestoneRows(env, uid, MILESTONE_LIMITS.profiles);
  if (!rows || rows.length !== 1) return null;
  const u = rows[0];
  return { first_name: u.first_name == null ? null : String(u.first_name), phone: String(u.phone || '').trim() || null,
    timezone: String(u.timezone || '').trim() || null };
}
// The item every later node reads: the chat row (spec 6.5), the lock (spec 6.4), and what the ping needs.
function milestoneReady(item, x, nowIso) {
  const body = buildMilestoneBody(item.msg_type, { firstName: x.profile ? x.profile.first_name : null, devices: x.devices });
  if (!body) return null;
  const trigger = MILESTONE_RULES.triggerPrefix + '_' + item.milestone;
  const badge = MILESTONE_BADGES[item.msg_type];
  return {
    milestone: item.milestone, msg_type: item.msg_type, withings_user_id: item.withings_user_id,
    care_period_id: item.care_period_id, task_id: x.task_id, sms: item.sms === true, copy_version: body.version,
    consent_obtained_at: x.consent_obtained_at, phone: x.profile ? x.profile.phone : null,
    timezone: x.profile ? x.profile.timezone : null, trigger_source: trigger,
    row: {
      withings_user_id: item.withings_user_id, care_period_id: item.care_period_id, task_id: x.task_id,
      channel: 'portal', direction: 'outbound', source: 'system', msg_type: item.msg_type, recipient_role: 'patient',
      status: 'sent', sent_at: nowIso, trigger_source: trigger, body: body.text,
    },
    lock: {
      care_period_id: item.care_period_id, withings_user_id: item.withings_user_id,
      template_code: MILESTONE_RULES.lockTemplate, task_template_id: null, assigned_to_id: null, measure_type: null,
      title: 'Milestone badge: ' + badge.name,
      description: 'Automated chat message with a badge, sent once per care period. Marks this badge as sent.',
      task_type: 'notification', clinical_domain: 'outreach', priority: 'low', status: 'completed',
      assigned_to_type: 'system', owner_role: 'system', visible_to_patient: false, available_at: nowIso, due_at: null,
      trigger_source: item.milestone === 'first_reading' ? 'observation' : 'care_team_manual',
      trigger_ref_type: 'task_instance', trigger_ref_id: x.task_id,
      dedupe_key: 'milestone:' + item.milestone + ':' + item.care_period_id,
      // `run` is how WF80 knows the lock is ITS lock: a dedupe hit returns the other run's row (spec 6.4).
      context_json: JSON.stringify({ milestone: item.milestone, msg_type: item.msg_type, run: String(x.run == null ? '' : x.run) }),
    },
  };
}
// Devices shipped (spec 4): WF17 fires once, so the caller's word is re-read, never trusted.
function decideShipped(input) {
  const item = input.item;
  const nowMs = Date.parse(input.now);
  const taskRows = milestoneRows(input.taskEnv, item.withings_user_id);
  if (!taskRows) return { ok: false, reason: 'ship_read_degraded' };
  const ship = milestoneShipTask(taskRows, item.withings_user_id, item.care_period_id, nowMs, item.source_task_id);
  if (!ship.ok) return ship;
  const period = milestoneEligiblePeriod(input.periodEnv, item.withings_user_id, item.care_period_id, input.programId);
  if (!period.ok) return period;
  const ready = milestoneReady(item, {
    task_id: ship.task_id, consent_obtained_at: period.consent_obtained_at, run: input.run,
    devices: milestoneDevicesOf(input.planEnv, item.withings_user_id),
    profile: milestoneProfileOf(input.profileEnv, item.withings_user_id),
  }, input.now);
  return ready ? { ok: true, ready } : { ok: false, reason: 'no_copy' };
}
// You're connected, gate 1 (spec 5.2): shipping done on this period, inside the window, before this reading.
// Run first: after day 21 it fails for good, so a patient who can no longer earn the badge costs two reads.
function milestoneShipGate(input) {
  const item = input.item;
  const shipRows = milestoneRows(input.shipEnv, item.withings_user_id, MILESTONE_LIMITS.ship);
  if (!shipRows) return { ok: false, reason: 'ship_read_degraded' };
  const ship = milestoneShipTask(shipRows, item.withings_user_id, item.care_period_id, Date.parse(input.now), null);
  if (!ship.ok) return ship;
  // A reading taken before shipping was done (a backlog sync, an owner's own cuff) never earns the badge (D4).
  if (!(Number(item.bp_group_at) > ship.completed_at)) return { ok: false, reason: 'reading_before_ship' };
  return ship;
}
// The rows of the last 24 h that mean "an alert just went out": WF2's crisis text and lane 4's call-911 reply.
function milestoneIsAlertRow(r) {
  const mt = String(r.msg_type || '');
  return mt === 'crisis' || (mt === 'acknowledgement' && /\(ack:emergency\)/.test(String(r.subject || '')));
}
// You're connected, gate 2 (D12): no open urgent alert, no alert sent to the patient in the last 24 h.
function milestoneLookback(input) {
  const uid = input.item.withings_user_id;
  const urgent = milestoneRows(input.urgentEnv, uid, MILESTONE_LIMITS.urgent);
  if (!urgent) return { ok: false, reason: 'urgent_read_degraded' };
  if (urgent.some((t) => String(t.template_code || '').indexOf('exc_') === 0 && String(t.priority || '') === 'urgent'
    && MILESTONE_OPEN_TASK.indexOf(String(t.status || '')) >= 0)) return { ok: false, reason: 'urgent_alert_open' };
  const recent = milestoneRows(input.recentEnv, uid, MILESTONE_LIMITS.recent);
  if (!recent) return { ok: false, reason: 'recent_read_degraded' };
  const since = Date.parse(input.now) - MILESTONE_RULES.lookbackHours * 3600000;
  if (recent.some((r) => {
    if (!milestoneIsAlertRow(r)) return false;
    const at = Date.parse(String(r.sent_at || r.created || ''));
    return !Number.isFinite(at) || at >= since;
  })) return { ok: false, reason: 'recent_alert' };
  return { ok: true };
}
// You're connected (spec 5.2, after the gate): both gates again (they are cheap, and the decision stands alone),
// then the period and the profile.
function decideFirstReading(input) {
  const item = input.item;
  const uid = item.withings_user_id;
  const ship = milestoneShipGate(input);
  if (!ship.ok) return ship;
  const look = milestoneLookback(input);
  if (!look.ok) return look;
  const period = milestoneEligiblePeriod(input.periodEnv, uid, item.care_period_id, input.programId);
  if (!period.ok) return period;
  const ready = milestoneReady(item, {
    task_id: ship.task_id, consent_obtained_at: period.consent_obtained_at, devices: null, run: input.run,
    profile: milestoneProfileOf(input.profileEnv, uid),
  }, input.now);
  return ready ? { ok: true, ready } : { ok: false, reason: 'no_copy' };
}
// MILESTONE-DECIDE-END

// MILESTONE-PING-START
// The only SMS (spec 6.6, D7): WF26's standard PHI-free "new message" line, word for word. Every doubt holds:
// the chat message alone is enough, and a held ping is never deferred.
const MILESTONE_PING = Object.freeze({
  quietFrom: 9, quietTo: 20, guardLimit: 200,
  rowBody: 'New message from your Withings care team - open the app to read it.',
});
const MILESTONE_APP_URL_RE = /^https:\/\/within\.gs\/access-patient(-beta)?$/;
function milestonePingSms(appUrl) {
  if (typeof appUrl !== 'string' || !MILESTONE_APP_URL_RE.test(appUrl)) return null;
  return 'You have a new message from your Withings care team. Open the app to read it: ' + appUrl
    + '\n\n- Withings Medical Care Team\n\nReply STOP to opt out.';
}
function milestoneLocalDay(tz, ms) {
  try {
    const p = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(ms));
    return /^\d{4}-\d{2}-\d{2}$/.test(p) ? p : null;
  } catch (e) { return null; }
}
function milestoneLocalHour(tz, ms) {
  try {
    const n = Number(new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', hourCycle: 'h23' }).format(new Date(ms)));
    return Number.isInteger(n) && n >= 0 && n <= 23 ? n : null;
  } catch (e) { return null; }
}
function decideMilestonePing(input) {
  const r = input.ready || {};
  const uid = String(r.withings_user_id || '');
  if (r.sms !== true) return { send: false, reason: 'sms_off' };
  if (!r.consent_obtained_at) return { send: false, reason: 'no_consent' };
  if (!r.phone) return { send: false, reason: 'no_phone' };
  const pEnv = input.patientEnv;
  if (!pEnv || pEnv.status !== 'success' || !Array.isArray(pEnv.data)) return { send: false, reason: 'patient_read_degraded' };
  const pat = pEnv.data.find((x) => x && String(x.withings_user_id != null ? x.withings_user_id : x.id_dev_user) === uid);
  if (!pat) return { send: false, reason: 'no_patient_row' };
  const opt = pat.sms_opt_in;
  if (opt === false || opt === 'false' || opt === 0 || opt === '0') return { send: false, reason: 'sms_opt_out' };
  const nowMs = Date.parse(input.now);
  // No timezone means no text: guessing Eastern texts Hawaii at 03:00.
  if (!r.timezone) return { send: false, reason: 'no_timezone' };
  const hour = milestoneLocalHour(r.timezone, nowMs);
  if (hour == null) return { send: false, reason: 'no_timezone' };
  if (hour < MILESTONE_PING.quietFrom || hour >= MILESTONE_PING.quietTo) return { send: false, reason: 'quiet_hours' };
  const oEnv = input.outreachEnv;
  if (!oEnv || oEnv.status !== 'success' || !Array.isArray(oEnv.data)) return { send: false, reason: 'ping_guard_degraded' };
  const count = Number(oEnv.meta && oEnv.meta.count);
  if ((Number.isFinite(count) && count > oEnv.data.length) || oEnv.data.length >= MILESTONE_PING.guardLimit) return { send: false, reason: 'ping_guard_degraded' };
  const today = milestoneLocalDay(r.timezone, nowMs);
  for (const x of oEnv.data) {
    if (!x || String(x.withings_user_id != null ? x.withings_user_id : x.id_dev_user) !== uid) return { send: false, reason: 'ping_guard_degraded' };
    if (String(x.status || '') !== 'sent' || String(x.channel || '') !== 'sms') continue;
    const at = Date.parse(String(x.sent_at || ''));
    // Any text on the patient's local day, from any lane (a coordinator's reply ping, a crisis text, ours):
    // this lane never adds a second one. It also covers WF26's 3 h gap: the window opens at 09:00, so a text
    // in the 3 h before is always on the same local day.
    if (!Number.isFinite(at) || (today && milestoneLocalDay(r.timezone, at) === today)) return { send: false, reason: 'texted_today' };
  }
  const message = milestonePingSms(input.appUrl);
  if (!message) return { send: false, reason: 'bad_app_url' };
  return { send: true, phone: r.phone, message };
}
// MILESTONE-PING-END

// Sentences reused word for word from already-approved text (spec 9). S-175 checks each one against its
// source, whitespace-normalised (the FAQ wraps its lines).
const REUSED_SENTENCES = [
  { text: 'Allow a few days; a tracking email follows.', source: 'api/workflows-target/task-nudge-faq.md' },
  { text: 'There is nothing to install: just turn the device on.', source: 'api/workflows-target/task-nudge-faq.md' },
  { text: 'Your care team uses your readings to catch problems early, coordinate your care, and keep you on track with your health goals.', source: 'app/public/surfaces/access/legal/access-beneficiary-notice.html' },
  { text: 'Electronic messages are not a substitute for professional medical care. For a medical emergency, call 911.', source: 'app/public/surfaces/access/legal/electronic-communication.html' },
];

// The kit's generated data files (scripts/sync-milestone-kit.mjs writes them; spec 8.1).
function kitIndex() {
  const badges = {};
  for (const msgType of Object.keys(MILESTONE_BADGES)) badges[msgType] = MILESTONE_BADGES[msgType].key + '.json';
  return { kit: MILESTONE_KIT, badges };
}
function kitDescriptor(msgType) {
  if (!milestoneOwn(MILESTONE_BADGES, msgType)) return null;
  const b = MILESTONE_BADGES[msgType];
  const slots = { first_name: { pattern: FIRST_NAME_RE.source, flags: 'u', max_length: 30, max_spaces: 2, nfkc_stable: true, optional: true, joiner: ', ' } };
  if (MILESTONE_COPY[msgType].some((c) => c.text.indexOf('{devices}') >= 0)) {
    slots.devices = { values: [DEVICE_PHRASES.both, DEVICE_PHRASES.bpm, DEVICE_PHRASES.scale, DEVICE_PHRASES.generic] };
  }
  return {
    kit: MILESTONE_KIT,
    badge: b.key,
    msg_type: msgType,
    name: b.name,
    art: b.art,
    template: 'badge.html',
    stylesheet: 'badge.css',
    accent: b.accent,
    slots,
    copy_versions: MILESTONE_COPY[msgType].map((c) => ({ version: c.version, text: c.text })),
    fallback: 'Show the message body as plain text.',
  };
}

export {
  MILESTONE_KIT, MILESTONE_BADGES, MILESTONE_COPY, DEVICE_PHRASES, FIRST_NAME_RE, MILESTONE_LABELS, REUSED_SENTENCES,
  MILESTONE_TRIGGER_PREFIX, isMilestonePing,
  devicesPhrase, cleanFirstName, fillMilestoneCopy, buildMilestoneBody, milestonePattern, isMilestoneRow,
  matchMilestone, milestoneLabelOf, isMilestoneSystemRow, labelMilestoneRow, kitIndex, kitDescriptor,
  MILESTONE_SWITCH_BADGES, parseMilestoneSwitch, milestoneSwitchAllows, milestoneHintsFromWf3, milestoneReadingCandidate,
  MILESTONE_READING_ALLOW, MILESTONE_MANUAL_ATTRIBS, MILESTONE_MANUAL_OK_PROGRAMS, milestoneGate, MILESTONE_RULES, milestoneRows, milestoneEligiblePeriod,
  milestoneShipTask, milestoneDevicesOf, milestoneProfileOf, milestoneReady, decideShipped, decideFirstReading, MILESTONE_LIMITS,
  milestoneShipGate, milestoneLookback, milestoneIsAlertRow,
  MILESTONE_PING, milestonePingSms, decideMilestonePing,
};
